import { useEffect, useState } from "react";
import axios from "axios";
import { CONTENT_ROUTES } from "../routes/content-routes";

function cleanString(value) {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function stripHtmlText(value) {
  if (typeof value !== "string") return "";
  const stripped = value
    .replace(/<br\s*\/?>(\r?\n)?/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim();

  // Decode HTML entities from WP-rendered content (e.g. &#8217; -> ').
  if (typeof document !== "undefined") {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = stripped;
    return textarea.value;
  }

  return stripped;
}

function toBoolean(value) {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1" || value === "true") return true;
  if (value === 0 || value === "0" || value === "false") return false;
  return undefined;
}

function shouldIncludeJob(job) {
  if (!job || typeof job !== "object") return false;

  // Supports an optional editor toggle field if one is present in ACF.
  const enabled =
    toBoolean(job.enabled) ??
    toBoolean(job.is_enabled) ??
    toBoolean(job.active) ??
    toBoolean(job.is_active);

  if (enabled === false) return false;

  const title = typeof job.title === "string" ? job.title.trim() : "";
  const summary = typeof job.summary === "string" ? job.summary.trim() : "";
  // If both core fields are blank, treat the row as deleted/hidden.
  return Boolean(title || summary);
}

/* Maps a single `job` custom post type entry into a job posting. */
function mapJobFromPost(post) {
  const acf = post?.acf && !Array.isArray(post.acf) ? post.acf : {};

  const title =
    cleanString(stripHtmlText(acf.title)) ??
    cleanString(stripHtmlText(post?.title?.rendered)) ??
    "";
  const description =
    cleanString(acf.summary) ??
    cleanString(stripHtmlText(post?.excerpt?.rendered)) ??
    cleanString(stripHtmlText(post?.content?.rendered)) ??
    "";

  if (!title && !description) return null;

  return {
    id: post?.id,
    slug: post?.slug,
    closingDate: cleanString(acf.closing_date),
    title,
    grade: cleanString(acf.grade),
    department: cleanString(acf.department),
    location: cleanString(acf.location) ?? "",
    type: cleanString(acf.type) ?? "",
    description,
    details: cleanString(acf.details) ?? cleanString(post?.content?.rendered),
  };
}

/* Maps the inline ACF job rows on the careers page into job postings. */
function mapCareersPageFromAcf(acf) {
  const source = acf && !Array.isArray(acf) ? acf : {};
  const jobs = source.jobs ?? {};

  const jobGroups = [
    jobs.job_1,
    jobs.job_2,
    jobs.job_3,
    jobs.job_4,
    jobs.job_5,
    jobs.job_6,
  ].filter(shouldIncludeJob);

  return {
    jobs: jobGroups.map((job, index) => ({
      // ACF rows have no post ID, so derive a stable slug-ish reference.
      slug: cleanString(job.slug) ?? String(index + 1),
      closingDate: cleanString(job.closing_date),
      title: job.title ?? "",
      grade: cleanString(job.grade),
      department: cleanString(job.department),
      location: job.location ?? "",
      type: job.type ?? "",
      description: job.summary ?? "",
      details: cleanString(job.details),
    })),
  };
}

/**
 * Loads the careers page copy (WordPress page + ACF) together with the job
 * postings. Jobs come from the `job` custom post type when it is available and
 * fall back to the inline ACF job rows on the careers page otherwise, so the
 * page works with either content setup.
 */
export const useCareersContent = () => {
  const [content, setContent] = useState(null);
  const [page, setPage] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function load() {
      try {
        const pageRes = await axios.get(
          CONTENT_ROUTES.POST_PAGE("/pages", "careers"),
        );
        const pageResponse = pageRes.data?.length ? pageRes.data[0] : {};
        const pageContent = mapCareersPageFromAcf(pageResponse?.acf);

        // The `job` CPT is optional — a 404 simply means no CPT is registered.
        let cptJobs = [];
        try {
          const jobsRes = await axios.get(
            CONTENT_ROUTES.JOBS(
              "?per_page=100&status=publish&orderby=date&order=desc&acf_format=standard",
            ),
          );
          cptJobs = (jobsRes.data ?? []).map(mapJobFromPost).filter(Boolean);
        } catch (err) {
          cptJobs = [];
        }

        if (!isMounted) return;

        setPage(pageResponse);
        setContent({
          ...pageContent,
          jobs: cptJobs.length ? cptJobs : pageContent.jobs,
        });
      } catch (error) {
        if (!isMounted) return;
        setIsError(true);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    load();

    return () => {
      isMounted = false;
    };
  }, []);

  return { content, page, isLoading, isError };
};
